import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface BenefitRow {
  readonly benefitId: string;
  readonly benefitCode: string;
  readonly title: string;
  readonly benefitType: string;
  readonly plannedBenefit: string;
  readonly actualBenefit: string | null;
  readonly status: string;
}

const TYPES = ['', 'FINANCIAL', 'OPERATIONAL', 'COMPLIANCE', 'PRODUCTIVITY', 'CUSTOMER', 'STRATEGIC'];
const STATUSES = ['', 'DEFINED', 'IN_PROGRESS', 'PARTIALLY_REALIZED', 'REALIZED', 'NOT_ACHIEVED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Benefit Realization Dashboard (§31 / §19). Register of benefit ledgers
 * (T444) tracking planned vs actual value and the defined→in-progress→realized
 * lifecycle (BR-06 measures realization against the configurable tolerance).
 * Supports defining a benefit and driving it — start-tracking and measure — each
 * wired to the guarded /v1/pm/benefits write API. Filterable by benefit type and
 * status.
 */
export function BenefitRealizationDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<BenefitRow[]>([]);
  const [benefitType, setBenefitType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('FINANCIAL');
  const [newPlanned, setNewPlanned] = useState('');
  const [actuals, setActuals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (benefitType) params.set('benefitType', benefitType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: BenefitRow[] }>(`/v1/pm/benefits${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, benefitType, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.benefit.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/benefits`, {
        method: 'POST',
        body: { title: newTitle.trim(), benefitType: newType, plannedBenefit: Number(newPlanned || 0) },
        token,
      });
      setNotice(t('m33.benefit.defined', { name: newTitle.trim() }));
      setNewTitle('');
      setNewPlanned('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'BENEFIT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: BenefitRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/benefits/${r.benefitId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.benefitCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="benefit-realization-dashboard">
      <h1>{t('m33.benefit.title')}</h1>

      <section aria-label="define-benefit">
        <h2>{t('m33.benefit.defineHeading')}</h2>
        <form aria-label="benefit-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-benefit-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-benefit-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m33.benefit.planned')}<input aria-label="field-planned-benefit" type="number" value={newPlanned} onChange={(e) => setNewPlanned(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-benefit" disabled={busy}>{busy ? t('m33.benefit.defining') : t('m33.benefit.defineBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="benefit-error">{actionError}</p>}
        {notice && <p aria-label="benefit-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-benefit-type" value={benefitType} onChange={(e) => setBenefitType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="benefit-count">{t('m33.benefit.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="benefit-table"
            rows={rows}
            rowKey={(r) => r.benefitId}
            columns={[
              { key: 'benefitCode', header: t('m33.common.code'), render: (r) => r.benefitCode },
              { key: 'title', header: t('m33.common.title'), render: (r) => r.title },
              { key: 'benefitType', header: t('m33.common.type'), render: (r) => r.benefitType },
              { key: 'plannedBenefit', header: t('m33.benefit.planned'), render: (r) => r.plannedBenefit },
              { key: 'actualBenefit', header: t('m33.benefit.actual'), render: (r) => r.actualBenefit ?? '—' },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DEFINED' && (
                    <Button variant="secondary" type="button" aria-label={`start-tracking-${r.benefitCode}`} onClick={() => void act(r, 'start-tracking', t('m33.benefit.act.trackingStarted'), 'BENEFIT_MANAGE')}>{t('m33.benefit.btnStartTracking')}</Button>
                  )}
                  {r.status === 'IN_PROGRESS' && (
                    <>
                      <input aria-label={`actual-benefit-${r.benefitCode}`} type="number" value={actuals[r.benefitId] ?? ''} onChange={(e) => setActuals((m) => ({ ...m, [r.benefitId]: e.target.value }))} placeholder={t('m33.benefit.actual')} />
                      <Button variant="secondary" type="button" aria-label={`measure-${r.benefitCode}`} onClick={() => void act(r, 'measure', t('m33.benefit.act.measured'), 'BENEFIT_MEASURE', { actualBenefit: Number(actuals[r.benefitId] || 0) })}>{t('m33.benefit.btnMeasure')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
